import * as React from 'react';
import { getChartOptionLabels, normaliseChartOptionLabel } from './chartOptionGroups';
import { useChartOptions } from './ChartOptionsContext';

const radioLabelSelector = '.MuiFormControlLabel-root';

// Presses the chart's own radio for each option the dashboard chose, exactly as a click on it would (so the chart's own onChange, query and
// drill-down state all behave as if the user had picked it). isEveryOptionFound is true once every wanted option has its radio on screen;
// hasChangedChart says a radio really was pressed (the chart was not already on that option).
const pressWantedRadios = (container: HTMLElement, wantedKeys: string[]) => {
    const radioLabels = Array.from(container.querySelectorAll<HTMLElement>(radioLabelSelector)).filter((radioLabel) =>
        radioLabel.querySelector('input[type="radio"]'),
    );
    if (radioLabels.length === 0) {
        return { isEveryOptionFound: false, hasChangedChart: false };
    }
    let isEveryOptionFound = true;
    let hasChangedChart = false;
    wantedKeys.forEach((wantedKey) => {
        const matchingLabel = radioLabels.find((radioLabel) => {
            const labelText = radioLabel.querySelector('.MuiFormControlLabel-label');
            return !!labelText && normaliseChartOptionLabel(labelText.textContent || '') === wantedKey;
        });
        const radioInput = matchingLabel && matchingLabel.querySelector<HTMLInputElement>('input[type="radio"]');
        if (!radioInput) {
            isEveryOptionFound = false;
            return;
        }
        if (!radioInput.checked) {
            radioInput.click();
            hasChangedChart = true;
        }
    });
    return { isEveryOptionFound, hasChangedChart };
};

// A chart card follows the dashboard's "Chart options": when a choice is applied (or the chart mounts after one was applied, because it
// scrolled into view later) its switch is set to that option. Only an option the chart's own switch has is applied (chartOptionsByFeature.ts),
// so every other chart keeps its setting. Once applied it leaves the chart alone, so a choice made on the chart afterwards stands.
// Returns a ref that says whether the dashboard changed this chart's switch, so a reset only starts again the charts it changed.
export const useApplyChartOptions = (itemRef: React.RefObject<HTMLElement>, featureName?: string) => {
    const { selections, version } = useChartOptions();
    const isChangedByDashboardRef = React.useRef(false);

    React.useEffect(() => {
        const container = itemRef.current;
        if (!container || !featureName || version === 0) {
            return undefined;
        }
        const offeredLabels = getChartOptionLabels(featureName);
        const wantedKeys = Object.keys(selections)
            .map((groupId) => selections[groupId as keyof typeof selections])
            .filter((optionKey): optionKey is string => !!optionKey && offeredLabels.includes(optionKey));
        if (wantedKeys.length === 0) {
            return undefined;
        }

        let isApplied = false;
        const apply = () => {
            if (isApplied) {
                // the chart reloads (its radios disappear while its skeleton shows): the next time they are back, apply again
                if (!container.querySelector(radioLabelSelector)) {
                    isApplied = false;
                }
                return;
            }
            const result = pressWantedRadios(container, wantedKeys);
            isApplied = result.isEveryOptionFound;
            if (result.hasChangedChart) {
                isChangedByDashboardRef.current = true;
            }
        };

        apply();
        // the radios only exist once the chart has its data, and a chart that has not scrolled into view has not mounted yet
        const observer = new MutationObserver(apply);
        observer.observe(container, { childList: true, subtree: true });
        return () => observer.disconnect();
    }, [itemRef, featureName, selections, version]);

    return isChangedByDashboardRef;
};
